import { useTranslation } from '@shared/core';
import React, { useMemo } from 'react';
import { MarqueeSlider } from '../../../../components/MarqueeSlider';
import { StyledContainer, StyledSubtitle, StyledTitle, styles } from './BrandsMarquee.styles';
import { BrandsMarqueeItem } from './BrandsMarqueeItem/BrandsMarqueeItem';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { useCatalogRouting } from '../../../../useCatalogRouting';
import { useRegistryOverviewTelemetry } from '@apps/registry/admin/routes/Overview/telemetry';
import { useShopStorefrontContext } from '../../../../components/ShopStorefrontProvider';

export interface BrandsMarqueeProps {
  showSkeleton?: boolean;
  skipTitle?: boolean;
  variant?: 'default' | 'overview';
}

export const BrandsMarquee: React.FC<BrandsMarqueeProps> = props => {
  const { showSkeleton, variant = 'default', skipTitle } = props;
  const isOverview = variant === 'overview';

  const { t } = useTranslation('catalogRegistry');
  const translations = t('brandsMarquee');
  const { goToBrandPage } = useCatalogRouting();
  const { config, isBabyStorefront } = useShopStorefrontContext();

  const brands = useMemo(() => {
    const storefrontBrands = config.brandMarqueeData;

    return [...storefrontBrands, ...storefrontBrands]; //to make sure there are enough elements to fit wide screens
  }, [config]);
  const telemetry = useRegistryOverviewTelemetry();

  const handleClick = (link: string, title: string) => {
    telemetry.registryOverviewInteraction({ component: 'Shop: Brands', method: 'Brand Clicked', label: title });
    if (link) {
      goToBrandPage(link);
    }
  };

  return (
    <StyledContainer
      {...(!isOverview && styles.container)}
      __css={{
        '.marquee-container > .overlay': {
          _before: {
            width: '200px',
            maxWidth: '10%',
            pointerEvents: 'none',
          },
          _after: {
            width: '200px',
            maxWidth: '10%',
            pointerEvents: 'none',
          },
        },
      }}
    >
      <SkeletonGroup
        isReady={!showSkeleton}
        placeholder={
          <SkeletonText
            rows={1}
            skeletonHeight={30}
            width={[200, null, 500]}
            alignSelf="center"
          />
        }
        display={'flex'}
        justifyContent={'center'}
        flexDirection={isOverview ? 'column' : 'row'}
      >
        {!skipTitle && (
          <StyledTitle {...styles.title(variant)}>
            {isOverview ? translations.titleOverview() : isBabyStorefront ? translations.babyRegistryTitle() : translations.weddingTitle()}
          </StyledTitle>
        )}
        {isOverview && (
          <StyledSubtitle
            __css={styles.subtitle}
            typographyVariant="body1"
          >
            {isBabyStorefront ? translations.babyRegistryTitle() : translations.weddingTitle()}
          </StyledSubtitle>
        )}
      </SkeletonGroup>
      <MarqueeSlider
        stopOnHover={true}
        gradient={true}
        speed={25}
      >
        {brands.map((brand, idx) => (
          <BrandsMarqueeItem
            key={idx}
            imageUrl={brand.imageUrl}
            showSkeleton={showSkeleton}
            onClick={() => handleClick(brand.link, brand.title)}
          />
        ))}
      </MarqueeSlider>
    </StyledContainer>
  );
};
